// CitedSummary — render an ATLAS call summary / KB-note body as real markdown
// with its `[^N]` citations resolved to transcript quotes.
//
// The body is agent markdown: headings, lists, bold, and inline `[^N]` footnote
// markers backed by a `[^N]: <transcript-line-id>` definition block. This
// component fetches the call's transcript lines, rewrites each definition to the
// verbatim quote it cites (`prepareSummary`), and renders the result with
// react-markdown + remark-gfm — so the markers become clickable superscripts and
// a "Citations" footnote list shows who said what (speaker + timestamp + quote).
//
// react-markdown does not render raw HTML by default, so the agent-authored body
// cannot inject markup. Replaces the raw `whitespace-pre-wrap` dumps that left
// `## headings` and dangling `[^N]` tokens on screen.

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import { prepareSummary, type CitationSource } from '@/lib/meeting-note-citations';

interface CitedSummaryProps {
    /** The markdown summary / KB-note body. */
    body: string;
    /** The call the summary belongs to — its transcript resolves the citations. */
    callId: string | null | undefined;
    className?: string;
}

const MARKDOWN_COMPONENTS: Components = {
    h1: (props) => (
        <h2
            className="text-card-title text-[rgb(var(--color-text-primary))] mt-4 mb-1.5 first:mt-0"
            {...props}
        />
    ),
    h2: (props) => {
        // remark-gfm labels the footnotes-section heading `footnote-label`;
        // swap it for our own "Citations" header below (rendered by `section`).
        if (props.id === 'footnote-label') return null;
        return (
            <h2
                className="text-card-title text-[rgb(var(--color-text-primary))] mt-4 mb-1.5 first:mt-0"
                {...props}
            />
        );
    },
    h3: (props) => (
        <h3
            className="text-body-medium font-semibold text-[rgb(var(--color-text-primary))] mt-3 mb-1"
            {...props}
        />
    ),
    p: (props) => <p className="my-1.5" {...props} />,
    ul: (props) => <ul className="list-disc pl-5 my-1.5 space-y-0.5" {...props} />,
    ol: (props) => <ol className="list-decimal pl-5 my-1.5 space-y-0.5" {...props} />,
    li: (props) => <li className="leading-relaxed" {...props} />,
    strong: (props) => (
        <strong className="font-semibold text-[rgb(var(--color-text-primary))]" {...props} />
    ),
    hr: () => <hr className="my-3 border-[rgb(var(--color-divider))]" />,
    code: (props) => (
        <code
            className="font-mono tabular-nums text-[0.85em] px-1 py-0.5 rounded bg-[rgb(var(--color-text-primary)/0.05)] text-[rgb(var(--color-text-secondary))]"
            {...props}
        />
    ),
    a: (props) => (
        <a className="text-[rgb(var(--color-accent))] no-underline hover:underline" {...props} />
    ),
    section: (props) => {
        const className = (props as { className?: string }).className ?? '';
        if (className.includes('footnotes')) {
            return (
                <section
                    {...props}
                    className="mt-4 pt-3 border-t border-[rgb(var(--color-divider))] text-annotation text-[rgb(var(--color-text-tertiary))]"
                >
                    <div className="text-card-title text-[rgb(var(--color-text-primary))] mb-1.5">
                        Citations
                    </div>
                    {props.children}
                </section>
            );
        }
        return <section {...props} />;
    },
};

export default function CitedSummary({ body, callId, className }: CitedSummaryProps) {
    const { data } = useQuery<CitationSource[]>({
        queryKey: ['call-citation-sources', callId],
        enabled: Boolean(callId),
        staleTime: 5 * 60_000,
        queryFn: async () => {
            const res = await api.deskPages.getCallCitationSources({
                params: { callId: callId as string },
            });
            if (res.status !== 200) throw new Error(`citation sources ${res.status}`);
            return res.body.sources;
        },
    });

    const prepared = useMemo(() => prepareSummary(body, data ?? []), [body, data]);

    return (
        <div
            className={cn(
                'text-body-medium text-[rgb(var(--color-text-secondary))] leading-relaxed',
                className,
            )}
        >
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={MARKDOWN_COMPONENTS}>
                {prepared.markdown}
            </ReactMarkdown>
            {prepared.orphanedMarkers.length > 0 && (
                <p className="mt-3 text-annotation text-[rgb(var(--color-text-tertiary))] italic">
                    {prepared.orphanedMarkers.length} citation
                    {prepared.orphanedMarkers.length === 1 ? '' : 's'} could not be linked to a
                    transcript line — this summary predates citation grounding.
                </p>
            )}
        </div>
    );
}
